import { useCallback, useEffect, useMemo, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { FileSpreadsheet, RefreshCw, Search, Wallet } from "lucide-react";
import { AppLayout, PageBackLink } from "../../../components/layout";
import { ContentCard, EmptyState, LoadingState, ui } from "../../../components/ui";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../components/ui/select";
import { TablePagination } from "../../leave-management/components/TablePagination";
import { cn } from "../../../lib/utils";
import {
  CLAIM_CATEGORY,
  CLAIM_CATEGORY_LABEL_KEY,
  CLAIM_STATUS,
  CLAIM_STATUS_LABEL_KEY,
  abbreviationFor,
  formatAmount,
  formatClaimPeriod,
  formatDate,
} from "../constants/claimConstants";
import {
  APPROVER_ACTION,
  CANONICAL_ROLE,
  DETAIL_ORIGIN,
  TEAM_CLAIMS_PATH,
  claimDetailPath,
} from "../constants/approvalConstants";
import { exportClaims, fetchAllClaims, markClaimsProcessed } from "../../../services/conveyanceClaimService";
import { ClaimStatusBadge } from "../components/ClaimStatusBadge";
import { ApproverActionModal } from "../components/ApproverActionModal";
import { Toast } from "../components/Toast";
import { selectBaseClass } from "../components/formControls";
import { useClaimCurrencies } from "../components/PolicyGate";
import {
  ApiPendingNotice,
  ApproverDeniedScreen,
  useApproverIdentity,
} from "../components/approverAccess";

const PAGE_SIZE = 10;
const ALL = "all";

const emptyResult = {
  rows: [],
  totalCount: 0,
  totals: [],
  filterOptions: { submitters: [], departments: [], months: [] },
};

/**
 * Screen 8 — All Conveyance Claims (Team HRMS).
 *
 * Finance's tenant-wide register, and the control screen for the back half of
 * the claim lifecycle. Two capabilities beyond the list:
 *
 *   Bulk Mark Processed — a status flip and nothing else. No payment date,
 *   reference or remark is captured, because payment execution happens in the
 *   tenant's banking system, outside Karya.
 *
 *   Export — the whole filtered set, with per-km trip-line detail, built
 *   server-side. Because Mark Processed stores no payment data, the export is
 *   the only artefact that makes a Processed flag auditable after the fact.
 *
 * Access is gated on the Finance_Manager canonical role. A Finance member also
 * approves from here when the chain includes a Finance step — clicking a claim
 * opens the same Screen 6 a manager gets.
 *
 * Filtering, ordering, paging and the totals all come from the API; this file
 * holds the filter state and renders the response.
 */
export function AllConveyanceClaimsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isFinanceManager, isReportingManager } = useApproverIdentity();
  const currencies = useClaimCurrencies();

  const [result, setResult] = useState(emptyResult);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");

  const [status, setStatus] = useState(ALL);
  const [month, setMonth] = useState(ALL);
  const [employeeId, setEmployeeId] = useState(ALL);
  const [departmentId, setDepartmentId] = useState(ALL);
  const [category, setCategory] = useState(ALL);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);

  const [selectedIds, setSelectedIds] = useState([]);
  const [processOpen, setProcessOpen] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [processError, setProcessError] = useState("");
  const [exporting, setExporting] = useState(false);
  const [toast, setToast] = useState(null);

  const filtersActive =
    status !== ALL ||
    month !== ALL ||
    employeeId !== ALL ||
    departmentId !== ALL ||
    category !== ALL ||
    search.trim() !== "";

  /** The query, shared by the list read and the export. */
  const params = useMemo(
    () => ({
      ...(status !== ALL && { status }),
      ...(month !== ALL && { month }),
      ...(employeeId !== ALL && { employeeId }),
      ...(departmentId !== ALL && { departmentId }),
      ...(category !== ALL && { category }),
      ...(search.trim() && { search: search.trim() }),
    }),
    [status, month, employeeId, departmentId, category, search]
  );

  const load = useCallback(() => {
    setLoading(true);
    setLoadError("");
    setSelectedIds([]);
    fetchAllClaims({ ...params, page: page - 1, size: PAGE_SIZE })
      .then(setResult)
      .catch((error) => {
        console.error("Failed to load conveyance claims register:", error);
        setResult(emptyResult);
        setLoadError(error.message || t("conveyanceApproval.errorLoadClaims"));
      })
      .finally(() => setLoading(false));
  }, [params, page, t]);

  useEffect(() => {
    if (isFinanceManager) load();
    else setLoading(false);
  }, [isFinanceManager, load]);

  /** Any filter change returns to page 1 — page 4 of the old result is meaningless. */
  const applyFilter = (setter) => (value) => {
    setter(value);
    setPage(1);
  };

  const resetFilters = () => {
    setStatus(ALL);
    setMonth(ALL);
    setEmployeeId(ALL);
    setDepartmentId(ALL);
    setCategory(ALL);
    setSearch("");
    setPage(1);
  };

  const { rows, totalCount, totals, filterOptions } = result;
  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));

  /**
   * Selection lives within the current page — the only rows a checkbox reaches.
   *
   * Only Approved claims may be processed. The split below drives the disabled
   * state and the "3 of 5 are not Approved" message; the server re-checks it
   * and is the authority, since a claim can move on between this page loading
   * and the action being sent.
   */
  const selected = rows.filter((row) => selectedIds.includes(String(row.claimId)));
  const eligible = selected.filter((row) => row.claimStatus === CLAIM_STATUS.APPROVED);
  const ineligibleCount = selected.length - eligible.length;
  const canProcess = selected.length > 0 && ineligibleCount === 0;

  const toggleRow = (claimId) => {
    const key = String(claimId);
    setSelectedIds((current) =>
      current.includes(key) ? current.filter((id) => id !== key) : [...current, key]
    );
  };

  const pageIds = rows.map((row) => String(row.claimId));
  const allOnPageSelected = pageIds.length > 0 && pageIds.every((id) => selectedIds.includes(id));
  const togglePage = () => setSelectedIds(allOnPageSelected ? [] : pageIds);

  const handleProcess = async () => {
    setProcessing(true);
    setProcessError("");
    try {
      await markClaimsProcessed(eligible.map((row) => row.claimId));
      setProcessOpen(false);
      setSelectedIds([]);
      setToast({
        tone: "success",
        message: t("conveyanceApproval.processedToast", { count: eligible.length }),
      });
      load();
    } catch (error) {
      setProcessError(
        error.messageKey
          ? t(error.messageKey)
          : error.message || t("conveyanceApproval.errorProcess")
      );
    } finally {
      setProcessing(false);
    }
  };

  /**
   * The export covers every claim matching the current filters, not the page
   * on screen, so the server builds it and this only hands the file over.
   */
  const handleExport = async () => {
    setExporting(true);
    try {
      const { blob, fileName } = await exportClaims(params);
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = fileName || "conveyance-claims.csv";
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Export failed:", error);
      setToast({
        tone: "error",
        message: error.messageKey
          ? t(error.messageKey)
          : error.message || t("conveyanceApproval.errorExport"),
      });
    } finally {
      setExporting(false);
    }
  };

  // The sidebar is built from the tenant's enabledServices tree, so whichever
  // submodule label it seeds can point either role at this route. A Manager
  // who lands here is sent to the queue they DO own rather than a dead end;
  // only someone holding neither role sees the denial.
  if (!isFinanceManager) {
    if (isReportingManager) return <Navigate to={TEAM_CLAIMS_PATH} replace />;
    return <ApproverDeniedScreen requiredRole={CANONICAL_ROLE.FINANCE_MANAGER} />;
  }

  const columns = [
    { key: "claimNo", label: t("conveyanceClaim.claimNo") },
    { key: "submitter", label: t("conveyanceApproval.submitter") },
    { key: "department", label: t("common.department") },
    { key: "category", label: t("conveyanceClaim.claimCategory") },
    { key: "period", label: t("conveyanceClaim.periodDate") },
    { key: "amount", label: t("conveyanceClaim.totalAmount"), className: "text-right" },
    { key: "status", label: t("conveyanceClaim.status") },
    { key: "approvedOn", label: t("conveyanceApproval.approvedOn") },
    { key: "processed", label: t("conveyanceApproval.processed") },
  ];

  return (
    <AppLayout>
      <PageBackLink to="/" />
      <div className="mb-6 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <h1 className={ui.pageTitle}>{t("conveyanceApproval.allClaimsTitle")}</h1>
        </div>
        <div className="flex shrink-0 items-center gap-2 self-end sm:self-auto">
          <button type="button" onClick={load} className={ui.filterBtn}>
            <RefreshCw className="h-4 w-4" />
            {t("formActions.refresh")}
          </button>
          <button
            type="button"
            onClick={handleExport}
            disabled={exporting || totalCount === 0}
            className={cn(ui.filterBtn, "disabled:opacity-50 disabled:cursor-not-allowed")}
          >
            <FileSpreadsheet className="h-4 w-4" />
            {exporting ? t("conveyanceApproval.exporting") : t("conveyanceApproval.export")}
          </button>
        </div>
      </div>

      <ApiPendingNotice />

      {loadError ? (
        <div className="mb-4 flex items-center justify-between gap-3 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3">
          <p className="text-sm font-semibold text-rose-700">{loadError}</p>
          <button
            type="button"
            onClick={load}
            className="inline-flex items-center gap-1.5 h-8 shrink-0 rounded-lg border border-rose-300 bg-white px-3 text-[11px] font-bold text-rose-700 hover:bg-rose-100 transition cursor-pointer"
          >
            <RefreshCw className="h-3.5 w-3.5" />
            {t("localConveyance.retry")}
          </button>
        </div>
      ) : null}

      {/* Filters — Employee, Status, Month, Department, Category, combinable. */}
      <div className="mb-4 flex flex-wrap items-center gap-2.5">
        <div className="relative w-full sm:w-[230px]">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[var(--color-text-secondary)]" />
          <input
            type="search"
            value={search}
            onChange={(event) => applyFilter(setSearch)(event.target.value)}
            placeholder={t("conveyanceApproval.searchPlaceholder")}
            className={cn(ui.input, "h-9 w-full pl-9")}
          />
        </div>

        <div className="w-[170px]">
          <Select value={employeeId} onValueChange={applyFilter(setEmployeeId)}>
            <SelectTrigger className={cn(selectBaseClass, "h-9 border-[var(--color-text-border)]")}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="max-h-60 overflow-y-auto">
              <SelectItem value={ALL}>{t("common.allEmployees")}</SelectItem>
              {(filterOptions?.submitters || []).map((option) => (
                <SelectItem key={option.value} value={String(option.value)}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="w-[150px]">
          <Select value={status} onValueChange={applyFilter(setStatus)}>
            <SelectTrigger className={cn(selectBaseClass, "h-9 border-[var(--color-text-border)]")}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>{t("conveyanceClaim.allStatuses")}</SelectItem>
              {Object.values(CLAIM_STATUS).map((value) => (
                <SelectItem key={value} value={value}>
                  {t(CLAIM_STATUS_LABEL_KEY[value])}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="w-[140px]">
          <Select value={month} onValueChange={applyFilter(setMonth)}>
            <SelectTrigger className={cn(selectBaseClass, "h-9 border-[var(--color-text-border)]")}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="max-h-60 overflow-y-auto">
              <SelectItem value={ALL}>{t("conveyanceClaim.allMonths")}</SelectItem>
              {(filterOptions?.months || []).map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="w-[170px]">
          <Select value={departmentId} onValueChange={applyFilter(setDepartmentId)}>
            <SelectTrigger className={cn(selectBaseClass, "h-9 border-[var(--color-text-border)]")}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="max-h-60 overflow-y-auto">
              <SelectItem value={ALL}>{t("conveyanceApproval.allDepartments")}</SelectItem>
              {(filterOptions?.departments || []).map((option) => (
                <SelectItem key={option.value} value={String(option.value)}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="w-[190px]">
          <Select value={category} onValueChange={applyFilter(setCategory)}>
            <SelectTrigger className={cn(selectBaseClass, "h-9 border-[var(--color-text-border)]")}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>{t("conveyanceClaim.allCategories")}</SelectItem>
              {Object.values(CLAIM_CATEGORY).map((value) => (
                <SelectItem key={value} value={value}>
                  {t(CLAIM_CATEGORY_LABEL_KEY[value])}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {filtersActive ? (
          <button type="button" onClick={resetFilters} className={ui.filterBtn}>
            {t("formActions.clearFilters")}
          </button>
        ) : null}
      </div>

      {/* Totals for the whole filtered set, so a payout batch can be tied to
          its value before the export is run. One line per snapshotted currency
          — claims submitted under different currencies are never added up. */}
      {!loading && totals?.length > 0 ? (
        <div className="mb-4 flex flex-wrap items-center gap-x-6 gap-y-2 rounded-xl border border-[var(--color-text-border)]/60 bg-white px-4 py-3">
          <p className={ui.sectionLabel}>
            {t("conveyanceApproval.filteredTotal", { count: totalCount })}
          </p>
          {totals.map((bucket) => (
            <p key={String(bucket.metricId)} className="text-sm font-extrabold text-foreground">
              {formatAmount(bucket.total, abbreviationFor(currencies, bucket.metricId))}
            </p>
          ))}
        </div>
      ) : null}

      {/* Selection bar — appears only with a selection, and states plainly why
          a mixed selection cannot be processed. */}
      {selected.length > 0 ? (
        <div className="mb-4 flex flex-col gap-2 rounded-xl border border-[var(--color-accent)]/25 bg-[var(--color-accent)]/5 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="min-w-0">
            <p className="text-sm font-semibold text-foreground">
              {t("conveyanceApproval.selectedCount", { count: selected.length })}
            </p>
            {ineligibleCount > 0 ? (
              <p className="mt-0.5 text-[12px] font-semibold text-rose-600">
                {t("conveyanceApproval.ineligibleSelection", {
                  ineligible: ineligibleCount,
                  total: selected.length,
                })}
              </p>
            ) : null}
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <button type="button" onClick={() => setSelectedIds([])} className={ui.filterBtn}>
              {t("conveyanceApproval.clearSelection")}
            </button>
            <button
              type="button"
              disabled={!canProcess}
              onClick={() => {
                setProcessError("");
                setProcessOpen(true);
              }}
              className={cn(ui.primaryBtn, "h-9 px-4 text-[13px]")}
            >
              {t("conveyanceApproval.markProcessed")}
            </button>
          </div>
        </div>
      ) : null}

      <ContentCard>
        {loading ? (
          <LoadingState message={t("conveyanceApproval.loadingClaims")} />
        ) : rows.length === 0 ? (
          <EmptyState
            icon={Wallet}
            title={
              filtersActive
                ? t("conveyanceApproval.emptyFilteredTitle")
                : t("conveyanceApproval.allEmptyTitle")
            }
            message={
              filtersActive
                ? t("conveyanceApproval.emptyFilteredMessage")
                : t("conveyanceApproval.allEmptyMessage")
            }
          />
        ) : (
          <>
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full text-sm" style={{ minWidth: "1120px" }}>
                <thead>
                  <tr className={ui.tableHead}>
                    <th className="px-4 py-3 w-[44px]">
                      <input
                        type="checkbox"
                        checked={allOnPageSelected}
                        onChange={togglePage}
                        aria-label={t("formActions.selectAll")}
                        className="h-4 w-4 cursor-pointer accent-[var(--color-primary)]"
                      />
                    </th>
                    {columns.map((column) => (
                      <th
                        key={column.key}
                        className={cn("px-4 py-3 font-semibold", column.className)}
                      >
                        {column.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-[var(--color-text-border)]/35">
                  {rows.map((claim) => {
                    const isSelected = selectedIds.includes(String(claim.claimId));
                    return (
                      <tr
                        key={claim.claimId}
                        className={cn(
                          "transition hover:bg-[var(--color-workspace)]/20",
                          isSelected && "bg-[var(--color-accent)]/[0.05]"
                        )}
                      >
                        <td className="px-4 py-3">
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => toggleRow(claim.claimId)}
                            aria-label={t("conveyanceApproval.selectClaim", {
                              claim: claim.claimNo || claim.claimId,
                            })}
                            className="h-4 w-4 cursor-pointer accent-[var(--color-primary)]"
                          />
                        </td>
                        <td
                          className="px-4 py-3 font-semibold text-[var(--color-primary)] cursor-pointer hover:underline"
                          onClick={() =>
                            navigate(claimDetailPath(claim.claimId, { from: DETAIL_ORIGIN.ALL }))
                          }
                        >
                          {claim.claimNo || `#${claim.claimId}`}
                        </td>
                        <td className="px-4 py-3 text-foreground">{claim.empName || "—"}</td>
                        <td className="px-4 py-3 text-[var(--color-text-secondary)]">
                          {claim.departmentName || "—"}
                        </td>
                        <td className="px-4 py-3 text-[var(--color-text-secondary)]">
                          {t(CLAIM_CATEGORY_LABEL_KEY[claim.category] || claim.category)}
                        </td>
                        <td className="px-4 py-3 text-[var(--color-text-secondary)]">
                          {formatClaimPeriod(claim, t)}
                        </td>
                        <td className="px-4 py-3 text-right font-semibold text-foreground">
                          {formatAmount(
                            claim.totalAmount,
                            abbreviationFor(currencies, claim.metricId)
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <ClaimStatusBadge status={claim.claimStatus} />
                        </td>
                        <td className="px-4 py-3 text-[11px] text-[var(--color-text-secondary)]">
                          {formatDate(claim.approvedOn, t) || "—"}
                        </td>
                        <td className="px-4 py-3 text-[12px] font-semibold text-[var(--color-text-secondary)]">
                          {claim.claimStatus === CLAIM_STATUS.PROCESSED
                            ? t("common.yes")
                            : t("common.no")}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Mobile cards — the checkbox stays, since Mark Processed is the
                point of this screen at any width. */}
            <div className="md:hidden divide-y divide-[var(--color-text-border)]/40">
              {rows.map((claim) => {
                const isSelected = selectedIds.includes(String(claim.claimId));
                return (
                  <div key={claim.claimId} className="flex items-start gap-3 p-4">
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => toggleRow(claim.claimId)}
                      aria-label={t("conveyanceApproval.selectClaim", {
                        claim: claim.claimNo || claim.claimId,
                      })}
                      className="mt-1 h-4 w-4 cursor-pointer accent-[var(--color-primary)]"
                    />
                    <button
                      type="button"
                      onClick={() =>
                        navigate(claimDetailPath(claim.claimId, { from: DETAIL_ORIGIN.ALL }))
                      }
                      className="min-w-0 flex-1 text-left"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <p className="text-sm font-bold text-foreground">
                            {claim.claimNo || `#${claim.claimId}`}
                          </p>
                          <p className="mt-0.5 truncate text-[12px] text-[var(--color-text-secondary)]">
                            {claim.empName || "—"}
                            {claim.departmentName ? ` · ${claim.departmentName}` : ""}
                          </p>
                        </div>
                        <ClaimStatusBadge status={claim.claimStatus} />
                      </div>
                      <p className="mt-2 text-lg font-extrabold text-foreground">
                        {formatAmount(
                          claim.totalAmount,
                          abbreviationFor(currencies, claim.metricId)
                        )}
                      </p>
                      <p className="text-[11px] text-[var(--color-text-secondary)]">
                        {t(CLAIM_CATEGORY_LABEL_KEY[claim.category] || claim.category)} ·{" "}
                        {formatClaimPeriod(claim, t)}
                      </p>
                    </button>
                  </div>
                );
              })}
            </div>

            <TablePagination
              page={page}
              totalPages={totalPages}
              totalItems={totalCount}
              pageSize={PAGE_SIZE}
              onPageChange={setPage}
            />
          </>
        )}
      </ContentCard>

      {/* Stated where Finance acts, not only in the spec: Processed carries no
          payment data, which is exactly why the export matters. */}
      <p className="mt-3 text-[11px] text-[var(--color-text-secondary)]">
        {t("conveyanceApproval.processedIsStatusOnlyNote")}
      </p>

      <ApproverActionModal
        action={APPROVER_ACTION.MARK_PROCESSED}
        open={processOpen}
        onOpenChange={setProcessOpen}
        onConfirm={handleProcess}
        loading={processing}
        error={processError}
        summary={
          <p className="font-semibold text-foreground">
            {t("conveyanceApproval.selectedCount", { count: eligible.length })}
          </p>
        }
      />

      {toast ? (
        <Toast tone={toast.tone} onDone={() => setToast(null)}>
          {toast.message}
        </Toast>
      ) : null}
    </AppLayout>
  );
}

export default AllConveyanceClaimsPage;
